/**
 * LiveSessionPage — the one screen everyone sees during a live session.
 *
 * Accessible at /prioritization/sessions/:sessionId/live
 *
 * No admin tabs. No config panels. One view for every user.
 * Facilitator gets a controls strip at the top. Everyone sees the same board.
 */

import { useSessionStartWording } from '../../../hooks/prioritization/useSessionStartWording';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Play,
  CheckCircle,
  Clock,
  Loader2,
  AlertCircle,
  X,
  Flag,
  Ruler,
  Tv,
  SlidersHorizontal,
} from 'lucide-react';
import {
  LiveSessionViewport,
  LiveLayerBar,
  LiveStatusPulse,
  LiveContextPanel,
  LiveRoomLayout,
  LiveFacilitatorBar,
  live,
  type LiveParticipantRow,
} from '../../../components/prioritization/session/liveExperience/LiveSessionChrome';
import { LiveSessionProgressProvider } from '../../../components/prioritization/session/liveExperience/LiveSessionProgressContext';
import { useLiveSessionProgress } from '../../../components/prioritization/session/liveExperience/LiveSessionProgressContext';
import { resolveSessionToolDescriptor } from '../../../lib/sessionToolCatalog';
import {
  buildLiveParticipantNameMap,
  fetchLiveParticipantNames,
  resolveLiveParticipantDisplayName,
} from '../../../components/prioritization/session/liveExperience/resolveLiveParticipantNames';
import { useSessionStore, runSessionViaRpc } from '../../../stores/prioritization/sessionStore';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import { rankingOutputForToolSession, useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useWriteBackOnClose } from '../../../hooks/prioritization/useWriteBackOnClose';
import { useSessionParticipantsStore } from '../../../stores/prioritization/sessionParticipantsStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { LiveToolRunner } from '../../../components/prioritization/session/LiveToolRunner';
import { RaiseConcernModal } from '../../../components/prioritization/session/RaiseConcernModal';
import { useFairnessFlagStore } from '../../../stores/prioritization/fairnessFlagStore';
import type { FairnessConcernType } from '../../../types/prioritization/fairnessFlag';
import { ScaleCalibrationPanel } from '../../../components/prioritization/session/ScaleCalibrationPanel';
import { ScaleCalibrationCard } from '../../../components/prioritization/session/ScaleCalibrationCard';
import { useScaleCalibrationStore } from '../../../stores/prioritization/scaleCalibrationStore';
import type { ToolItem } from '../../../contexts/ToolItemsContext';
import { getOutputTypeForSessionTool } from '../../../lib/toolRegistry';
import { normalizeSessionToolId } from '../../../lib/sessionToolCatalog';
import { notifySessionLiveNow } from '../../../lib/notificationService';
import { ensureSessionJoinable } from '../../../lib/sessionGoLive';
import { resolveLaunchTool } from '../../../lib/sessions/launchTool';
import { joinPathFor } from '../../../lib/sessions/runItLive';
import { JoinQr } from '../../../components/prioritization/session/JoinQr';
import {
  aggregateBarPercent,
  formatAggregateMetric,
  formatMyResultMetric,
  isParticipantResponseSubmitted,
  sortAggregatesForDisplay,
  sortMyResultsForDisplay,
  roomIsAllIn,
} from '../../../lib/liveSessionDisplay';
import type { ToolOutputType, ToolResultItem, AggregateResult } from '../../../types/prioritization/toolSession';
import { FacilitatorConductorPanel } from '../../../components/prioritization/session/FacilitatorConductorPanel';
import { RoomReasonCapture } from '../../../components/prioritization/deliberation/RoomReasonCapture';
import { RoomCountdown } from '../../../components/prioritization/session/RoomCountdown';
import { useRoomReconnect } from '../../../hooks/useRoomReconnect';
import { namesHidden, useRoomTimer, useSessionRoomRules } from '../../../lib/sessions/roomRules';
import { SessionPausedOverlay } from '../../../features/sessions-participant/components/SessionPausedOverlay';
import { ItemSpotlightDrawer } from '../../../features/sessions-participant/components/ItemSpotlightDrawer';
import { AnnouncementBanner } from '../../../features/sessions-participant/components/AnnouncementBanner';
import {
  usePresenceStore,
  useSessionPresence,
  type PresenceActivity,
} from '../../../stores/prioritization/presenceStore';
import { getV3StepContext } from '../../../features/session-engine-v3/stepContext';
import type { StepId } from '../../../features/session-engine-v3/types';
import {
  attachSessionToResolvedLoop,
  catchballQueryFromSearch,
  withCatchballQuery,
} from '../../../lib/catchballSessionLink';
import { useMobileExperience } from '../../../mobile/featureFlag';
import { collectReadinessBlockers } from '../../../lib/trackReadinessEvaluator';

// ─── Helpers ───────────────────────────────────────────────────────────────

function isV3StepId(value: string | null | undefined): value is StepId {
  return value === 'S1' || value === 'S2' || value === 'S3' || value === 'S4' || value === 'S5';
}

async function markParentV3StepComplete(modeConfig: unknown): Promise<void> {
  const v3Config = (modeConfig as Record<string, unknown> | undefined)?.v3Config as
    | { parentEventId?: string; parentStep?: StepId }
    | undefined;
  if (!v3Config?.parentEventId || !v3Config.parentStep) return;

  const supabase = getSafeSupabaseClient();
  if (!supabase) return;

  const { data } = await supabase
    .from('prioritization_sessions')
    .select('mode_config')
    .eq('id', v3Config.parentEventId)
    .maybeSingle();

  const parentModeConfig = (data as { mode_config?: Record<string, unknown> } | null)?.mode_config ?? {};
  const parentV3 = (parentModeConfig.v3Config as Record<string, unknown> | undefined) ?? {};
  const parentSteps = (parentV3.steps as Record<StepId, Record<string, unknown>> | undefined) ??
    ({} as Record<StepId, Record<string, unknown>>);
  const currentStep = parentSteps[v3Config.parentStep] ?? {};

  await supabase
    .from('prioritization_sessions')
    .update({
      mode_config: {
        ...parentModeConfig,
        v3Config: {
          ...parentV3,
          steps: {
            ...parentSteps,
            [v3Config.parentStep]: {
              ...currentStep,
              runStatus: 'complete',
            },
          },
          stepHistory: [
            ...((parentV3.stepHistory as unknown[]) ?? []),
            { step: v3Config.parentStep, action: 'run', timestamp: new Date().toISOString(), note: 'Child session completed' },
          ],
        },
      },
    })
    .eq('id', v3Config.parentEventId);
}

function avatarInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Types ─────────────────────────────────────────────────────────────────

interface LiveBoardProps {
  sessionId: string;
  toolSessionId: string;
  /**
   * Items for the session — pre-validated by the outer page shell.
   * Guaranteed to be non-empty when LiveBoard renders.
   */
  sessionToolItems: ToolItem[];
  /** Open the ranking tool immediately (from Session detail "Start my ranking") */
  autoStartRanking?: boolean;
  onRankingStarted?: () => void;
}

// ─── Live Board (shared by all users) ──────────────────────────────────────

function LiveBoard({ sessionId, toolSessionId, sessionToolItems, autoStartRanking, onRankingStarted }: LiveBoardProps) {
  const navigate = useNavigate();
  const writeBackOnClose = useWriteBackOnClose();
  const [boardSearchParams] = useSearchParams();
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? '';

  const sessions = useSessionStore(s => s.sessions);
  const session = sessions.find(s => s.id === sessionId);

  const participantResponses = useToolSessionStore(s => s.participantResponses);
  const computeAggregates = useToolSessionStore(s => s.computeAggregates);
  const subscribeToToolSession = useToolSessionStore(s => s.subscribeToToolSession);
  const toolSessions = useToolSessionStore(s => s.toolSessions);

  const allParticipants = useSessionParticipantsStore(s => s.participants);
  const fetchParticipantsForSession = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const { users: systemUsers } = useSystemUsers({ workspaceId: session?.workspaceId ?? null });

  // Load session participants on mount so the board has accurate data
  useEffect(() => {
    void fetchParticipantsForSession(sessionId);
  }, [sessionId, fetchParticipantsForSession]);

  // Aggressive polling fallback — directly fetch responses every 4 s.
  // Supabase Realtime postgres_changes + complex RLS EXISTS subqueries can
  // silently drop cross-user events. This ensures every viewer sees the
  // current state regardless of whether Realtime fires.
  const upsertParticipantResponse = useToolSessionStore(s => s._upsertParticipantResponse);
  const upsertToolSession = useToolSessionStore(s => s._upsertToolSession);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const supabase = getSafeSupabaseClient();
      if (!supabase || cancelled) return;

      // Fetch tool session row (phase, participant list)
      const { data: tsRow } = await supabase
        .from('tool_sessions')
        .select('*')
        .eq('id', toolSessionId)
        .single();
      if (tsRow && !cancelled) {
        // rowToToolSession is internal — use the store's subscribe path instead
        void subscribeToToolSession(toolSessionId);
      }

      // Fetch ALL responses for this tool session
      const { data: respRows } = await supabase
        .from('tool_session_responses')
        .select('*')
        .eq('tool_session_id', toolSessionId);
      if (!respRows || cancelled) return;

      const toolSession = useToolSessionStore.getState().toolSessions.find(s => s.id === toolSessionId);
      respRows.forEach((row, idx) => {
        const colorIdx = toolSession
          ? toolSession.participantIds.indexOf(row.participant_id as string)
          : idx;
        upsertParticipantResponse(
          {
            id: row.id as string,
            toolSessionId: row.tool_session_id as string,
            participantId: row.participant_id as string,
            participantName: (row.participant_name as string) || '',
            participantInitials: (row.participant_name as string || 'U').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
            participantColor: '',
            submittedAt: (row.submitted_at as string) ?? (row.created_at as string),
            results: ((row.results as Array<{ id: string; name?: string; rank?: number; score?: number }>) ?? [])
              .map(r => ({ ...r, name: r.name ?? r.id })),
            isComplete: Boolean(row.is_complete),
          },
          colorIdx >= 0 ? colorIdx : idx
        );
      });
    };

    const interval = setInterval(() => { void poll(); }, 4000);
    // Run immediately on mount too
    void poll();
    return () => { cancelled = true; clearInterval(interval); };
  }, [toolSessionId, subscribeToToolSession, upsertParticipantResponse, upsertToolSession]);

  const [isClosing, setIsClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [showingTool, setShowingTool] = useState(false);
  const [rankingError, setRankingError] = useState<string | null>(null);

  const toolSession = toolSessions.find(s => s.id === toolSessionId);

  // For V3 sessions, selectedToolId may not be set on the session row itself —
  // the tool was stored in v3Config. Fall back to the tool_sessions row's toolType.
  const effectiveToolId: string | null | undefined =
    session?.selectedToolId ?? toolSession?.toolType ?? null;

  const outputType: ToolOutputType = useMemo(() => {
    const canonical = normalizeSessionToolId(effectiveToolId);
    return getOutputTypeForSessionTool(canonical ?? undefined);
    // effectiveToolId falls back to toolSession.toolType for V3 sessions (selectedToolId
    // is often unset until the tool session row loads) — must recompute once it arrives,
    // otherwise the aggregate/submission-detection logic gets stuck on a stale output type.
  }, [effectiveToolId]);

  const isSubmitted = useCallback(
    (r: (typeof participantResponses)[number]) =>
      isParticipantResponseSubmitted(r, outputType),
    [outputType]
  );

  // The current participant's own submitted response — derived directly from the
  // shared store rather than tracked in separate state. Each participant's entry
  // in `participantResponses` is keyed by (toolSessionId, participantId) and is
  // only ever replaced by an upsert to that SAME participant's row (see
  // toolSessionStore._upsertParticipantResponse), so this reference stays stable
  // when other participants submit — it only changes when *this* user's own
  // response changes. That stability is what keeps "my results" from visually
  // getting clobbered when someone else submits after I do.
  const myResponse = useMemo(
    () =>
      participantResponses.find(
        r => r.toolSessionId === toolSessionId && r.participantId === userId && isSubmitted(r)
      ),
    [participantResponses, toolSessionId, userId, isSubmitted]
  );
  const mySubmitted = myResponse != null;

  // Subscribe to realtime updates on mount. `responsesReady` flips true once the
  // initial fetch (tool session + all participant responses) has resolved at
  // least once, so we know whether *this* participant already submitted before
  // deciding whether to auto-enter the tool below.
  const [responsesReady, setResponsesReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void subscribeToToolSession(toolSessionId).then(() => {
      if (!cancelled) setResponsesReady(true);
    });
    return () => { cancelled = true; };
  }, [toolSessionId, subscribeToToolSession]);

  const responses = useMemo(
    () =>
      participantResponses.filter(
        r => r.toolSessionId === toolSessionId && isSubmitted(r)
      ),
    [participantResponses, toolSessionId, isSubmitted]
  );

  // Depend on the raw participantResponses array (not responses.length) so a
  // response that gets revised/upserted in place — same submitted count, new
  // results — still triggers a recompute. computeAggregates reads the live
  // store state itself via get(), so ESLint can't see that this dependency
  // matters; it's the only thing that tells React the store's response data
  // has actually changed since the last render.
  const aggregates = useMemo(
    () => computeAggregates(toolSessionId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [computeAggregates, toolSessionId, participantResponses]
  );

  const sortedAgg = useMemo(
    () => sortAggregatesForDisplay(aggregates, outputType),
    [aggregates, outputType]
  );

  const sessionParticipants = allParticipants.filter(p => p.sessionId === sessionId);

  // toolSession.participantIds is the authoritative list — the facilitator populated it
  // with all participants when opening the session. session_participants RLS may only
  // return a subset (e.g. just the current user's own row), so never use it as the
  // primary source for who is in the session.
  const displayParticipantIds: string[] = toolSession?.participantIds.length
    ? toolSession.participantIds
    : sessionParticipants.map(p => p.userId);

  const totalExpected = displayParticipantIds.length;

  const toolSessionResponses = useMemo(
    () => participantResponses.filter(r => r.toolSessionId === toolSessionId),
    [participantResponses, toolSessionId]
  );

  const [fetchedParticipantNames, setFetchedParticipantNames] = useState<Record<string, string>>({});

  const participantNameById = useMemo(
    () =>
      buildLiveParticipantNameMap({
        participantIds: displayParticipantIds,
        systemUsers,
        responseNames: toolSessionResponses.map(r => ({
          participantId: r.participantId,
          participantName: r.participantName,
        })),
        currentUserId: userId,
        currentUserName: userName,
        extraNames: fetchedParticipantNames,
      }),
    [
      displayParticipantIds,
      systemUsers,
      toolSessionResponses,
      userId,
      userName,
      fetchedParticipantNames,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const fetched = await fetchLiveParticipantNames(displayParticipantIds, participantNameById);
      if (cancelled || Object.keys(fetched).length === 0) return;
      setFetchedParticipantNames(prev => ({ ...prev, ...fetched }));
    })();
    return () => { cancelled = true; };
  }, [displayParticipantIds, participantNameById]);

  const isFacilitator = session?.facilitatorId === userId ||
    sessionParticipants.some(p => p.userId === userId && p.role === 'facilitator');

  // ── Presence: everyone on this board is in the room ─────────────────────
  // The conductor's "Who's in the room" reads this channel. Join once the
  // session is known so the role is right the first time.
  useSessionPresence(
    session ? sessionId : null,
    userId,
    userName || (isFacilitator ? 'Facilitator' : 'Participant'),
    isFacilitator ? 'facilitator' : 'participant',
  );
  // S-7: a dropped connection picks itself back up, without a reload.
  useRoomReconnect(session ? sessionId : null, {
    userId,
    userName: userName || (isFacilitator ? 'Facilitator' : 'Participant'),
    role: isFacilitator ? 'facilitator' : 'participant',
  });
  const presenceConnected = usePresenceStore(s => s.isConnected);
  const updatePresenceActivity = usePresenceStore(s => s.updateActivity);
  const presenceActivity: PresenceActivity = showingTool
    ? 'ranking'
    : mySubmitted
      ? 'submitted'
      : isFacilitator
        ? 'observing'
        : 'waiting';
  // Re-sent on connect too: an update made before the channel is up is dropped.
  useEffect(() => {
    if (presenceConnected) void updatePresenceActivity(presenceActivity);
  }, [presenceConnected, presenceActivity, updatePresenceActivity]);

  // ── The host's bar and controls (Doc 26; SPEC-01 §5.2; names per ruling R5) ──
  const [showHostControls, setShowHostControls] = useState(false);
  // The host's controls open below the host's bar, so Close & save, Board and
  // Join link stay in reach while they are open. The drawer used to start at
  // the top of the window and cover the bar.
  const hostBarRef = useRef<HTMLDivElement | null>(null);
  const [hostBarBottom, setHostBarBottom] = useState(0);
  useEffect(() => {
    if (!showHostControls) return;
    const el = hostBarRef.current;
    const measure = () => setHostBarBottom(el ? Math.max(0, Math.round(el.getBoundingClientRect().bottom)) : 0);
    measure();
    window.addEventListener('resize', measure);
    const observer = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && el) observer.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [showHostControls]);
  const [showJoin, setShowJoin] = useState(false);
  const [joinCopied, setJoinCopied] = useState(false);
  const [isPausedLive, setIsPausedLive] = useState(false);

  // Track pause state from session_events so the conductor's Pause/Resume
  // toggle reflects reality (same channel pattern as SessionPausedOverlay).
  useEffect(() => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !sessionId) return;
    const ch = supabase
      .channel(`pause-facilitator:${sessionId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
        payload => {
          const ev = payload.new as { event_type: string };
          if (ev.event_type === 'session_paused') setIsPausedLive(true);
          if (ev.event_type === 'session_resumed') setIsPausedLive(false);
        }
      )
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [sessionId]);

  // ── Process-fairness flag (Gap 3) ───────────────────────────────────────
  const raiseFairnessFlag = useFairnessFlagStore(s => s.raiseFlag);
  const [showConcernModal, setShowConcernModal] = useState(false);
  const [concernSubmitting, setConcernSubmitting] = useState(false);
  const [concernError, setConcernError] = useState<string | null>(null);
  const [concernSubmitted, setConcernSubmitted] = useState(false);

  const handleRaiseConcern = async (concernType: FairnessConcernType, note: string) => {
    if (!sessionId) return;
    setConcernSubmitting(true);
    setConcernError(null);
    const { error } = await raiseFairnessFlag({
      sessionId,
      toolSessionId,
      raisedBy: userId,
      raisedByName: userName,
      concernType,
      note,
    });
    setConcernSubmitting(false);
    if (error) {
      setConcernError(error);
      return;
    }
    setConcernSubmitted(true);
    setShowConcernModal(false);
  };

  // ── Scale calibration (Gap 7) ───────────────────────────────────────────
  const fetchCalibrations = useScaleCalibrationStore(s => s.fetchForSession);
  const calibrations = useScaleCalibrationStore(s => s.calibrations);
  const [showCalibrationPanel, setShowCalibrationPanel] = useState(false);
  const [showCalibrationCard, setShowCalibrationCard] = useState(false);

  useEffect(() => {
    if (sessionId) void fetchCalibrations(sessionId);
  }, [sessionId, fetchCalibrations]);

  const openCalibrationCount = sessionId
    ? calibrations.filter(c => c.sessionId === sessionId && c.status === 'open').length
    : 0;

  // ── Facilitator: close session ──────────────────────────────────────────

  const handleClose = async () => {
    if (!session) return;
    setIsClosing(true);
    setCloseError(null);
    try {
      if (!getSafeSupabaseClient()) throw new Error('Database not available');

      // For S3 criteria sessions, results use criterion IDs (not workspace
      // UUIDs) — they are counted as given, never mapped onto workspace items.
      const v3Config = (session.modeConfig as unknown as Record<string, unknown> | undefined)
        ?.v3Config as { primaryGoalStep?: string; parentStep?: string; criteria?: Array<{ id: string; name: string; weight: number }> } | undefined;
      const activeStepForOutput = isV3StepId(v3Config?.parentStep) ? v3Config.parentStep : v3Config?.primaryGoalStep;
      const isCriteriaSession = activeStepForOutput === 'S3';

      // The one counting rule (rankingOutputForToolSession): re-reads the
      // answers from the database and counts them exactly as this board shows
      // them, so what the room watched is what gets saved.
      const toolId = session.selectedToolId ?? 'pairwise_ranking';
      const rankingOutput = await rankingOutputForToolSession(toolSessionId, {
        toolId,
        toolName: toolId,
        keepResultIds: isCriteriaSession,
      });

      if (!rankingOutput) {
        throw new Error(
          responses.length > 0
            ? 'Responses are still syncing — wait a moment and try Close & save again.'
            : `No responses yet (${responses.length} of ${totalExpected} submitted). Each participant must tap Start ranking, complete the tool, then submit their response.`
        );
      }
      const { rankedItemIds } = rankingOutput;
      const itemScores: Record<string, number> = Object.fromEntries(
        Object.entries(rankingOutput.scores).map(([id, s]) => [id, s.total]),
      );

      // Write ranking_output into Zustand BEFORE transitionSessionState.
      // transitionSessionState calls syncToSupabase which upserts the entire
      // session object — if rankingOutput is null in Zustand it overwrites DB.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      useSessionStore.getState().setRankingOutput(session.id, rankingOutput as any);

      if (isCriteriaSession) {
        // S3 criteria session: write ranked weights back into v3Config.criteria so
        // S4 (Values) and S5 (Ranking) can use them. Skip session_results (not workspace items).
        const existingCriteria = v3Config?.criteria ?? [];
        const totalScore = Object.values(itemScores).reduce((a, b) => a + b, 0) || 1;
        const updatedCriteria = existingCriteria.map(c => ({
          ...c,
          weight: Math.round(((itemScores[c.id] ?? 0) / totalScore) * 100),
        }));
        // Persist updated criteria weights back to v3Config
        await useSessionStore.getState().updateModeConfig(session.id, {
          v3Config: { ...v3Config, criteria: updatedCriteria, criteriaRanking: rankedItemIds },
        } as never);

        // Also write ranking_output to the DB session row directly so the detail
        // page can read it after fetchFromSupabase re-hydrates the Zustand store.
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          await supabase
            .from('prioritization_sessions')
            .update({ ranking_output: rankingOutput })
            .eq('id', session.id);
        }

        // Close the tool session so it no longer appears as "live"
        await useToolSessionStore.getState().setPhase(toolSessionId, 'finalized');
        await markParentV3StepComplete(session.modeConfig);

        // Refresh the session so the designer shows the updated criteria weights
        await useSessionStore.getState().fetchFromSupabase();

        const inboxCtx = catchballQueryFromSearch(boardSearchParams);
        const attach = await attachSessionToResolvedLoop({
          sessionId: session.id,
          urlLoopId: inboxCtx.loopId,
          trackId: session.trackId ?? inboxCtx.trackId,
          cycleId: session.cycleId ?? inboxCtx.cycleId,
        });
        const loopId = attach.loopId;

        // Navigate to the session result detail page — S3 is one step, not the final result,
        // but the facilitator and participants still deserve to see what was decided.
        // Keep loopId/ballId so the ball owner can return to Catchball.
        navigate(
          withCatchballQuery(`/prioritization/reports/session/${session.id}`, {
            ...inboxCtx,
            loopId,
          }),
          { state: { lockMoment: true, sessionAttachFailure: attach.ok ? undefined : attach.failure } },
        );
        return;
      }

      // Item ranking session: one act, one write path (771). `run_session`
      // writes ranking_output, the RUN_COMPLETE state and the session_results
      // rows in ONE server transaction, so a refusal leaves the session open
      // with nothing saved, and an accepted close can never be missing its
      // result rows. (Non-UUID V3/demo ids live only in ranking_output — the
      // RPC keeps that rule.)
      const runResult = await runSessionViaRpc(session.id, rankingOutput);
      if (!runResult.success) {
        // The RPC returns the whole readiness payload with a blocker per
        // unmet condition, and this path threw away everything but the one
        // sentence — "Prerequisites not met for this ranking session" — so a
        // room that had just scored 630 values was told nothing about which
        // prerequisite, and someone had to query the database to find out.
        // SessionDetail's Run tab already reads these; this path did not.
        const blockers = collectReadinessBlockers(runResult.readiness);
        const detail = blockers.length > 0 ? ` ${blockers.join('. ')}.` : '';
        throw new Error(
          `Could not save results: ${runResult.error ?? 'the server refused the run'}.${detail} Session remains open.`,
        );
      }

      // V-5: the workflow's write-back setting decides what the per-criterion scores become.
      await writeBackOnClose({ session, toolSessionId });

      // Refresh store so Session Results index shows RUN_COMPLETE + ranking_output
      await useSessionStore.getState().fetchFromSupabase();
      await markParentV3StepComplete(session.modeConfig);

      // Append this sitting + results onto Catchball (loop.sessionIds). The
      // session's own track/cycle are passed so resolution can fall back when
      // the close happened outside a desk link — without them this silently
      // attached nothing.
      const inboxCtx = catchballQueryFromSearch(boardSearchParams);
      const attach = await attachSessionToResolvedLoop({
        sessionId: session.id,
        urlLoopId: inboxCtx.loopId,
        trackId: session.trackId ?? inboxCtx.trackId,
        cycleId: session.cycleId ?? inboxCtx.cycleId,
      });
      const loopId = attach.loopId;

      // Lock moment (Doc 26 rule 6): land on the produced artifact, not the list.
      // Keep desk query so ← Back to Catchball stays available for the ball owner.
      navigate(
        withCatchballQuery(`/prioritization/reports/session/${session.id}`, {
          ...inboxCtx,
          loopId,
        }),
        { state: { lockMoment: true, sessionAttachFailure: attach.ok ? undefined : attach.failure } },
      );
    } catch (err) {
      setCloseError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsClosing(false);
    }
  };

  // ── My ranking ──────────────────────────────────────────────────────────

  const liveWorkspaceId =
    session?.workspaceId ?? toolSession?.workspaceId ?? getCurrentWorkspaceId() ?? '';

  const handleStartRanking = () => {
    setRankingError(null);
    if (!liveWorkspaceId) {
      setRankingError('Workspace context is missing — cannot start ranking.');
      return;
    }
    setShowingTool(true);
  };

  // Deep-link from session detail (?startRanking=1) — open the tool immediately.
  // sessionToolItems is guaranteed non-empty by the outer page shell, so no
  // item-readiness check is needed here.
  useEffect(() => {
    if (!autoStartRanking || mySubmitted) return;
    setShowingTool(true);
    onRankingStarted?.();
  }, [autoStartRanking, mySubmitted, onRankingStarted]);

  // A participant who hasn't submitted yet (and didn't arrive via the
  // ?startRanking=1 deep link above) should land directly in the tool instead
  // of the aggregate/"Live aggregate" board — they have nothing of their own to
  // see there yet. This fires once per mount, gated on `responsesReady` so we
  // don't misfire before we've confirmed whether they already submitted in a
  // previous visit (which would otherwise flash them back into the tool).
  // A deliberate "← Board" click afterwards is respected — this never re-fires.
  const hasAutoEnteredToolRef = useRef(false);
  useEffect(() => {
    if (autoStartRanking) return; // handled by the deep-link effect above
    if (hasAutoEnteredToolRef.current) return;
    if (!responsesReady) return;
    if (showingTool || mySubmitted) {
      hasAutoEnteredToolRef.current = true;
      return;
    }
    if (!liveWorkspaceId) return; // wait for workspace context to resolve
    hasAutoEnteredToolRef.current = true;
    setShowingTool(true);
  }, [autoStartRanking, responsesReady, showingTool, mySubmitted, liveWorkspaceId]);

  const handleToolComplete = () => {
    setRankingError(null);
    setShowingTool(false);
  };

  if (!session) {
    return (
      <LiveSessionViewport>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className={`h-8 w-8 ${live.textTer} animate-spin`} />
        </div>
      </LiveSessionViewport>
    );
  }

  const maxScore =
    outputType === 'classification'
      ? 1
      : outputType === 'scoring'
        ? sortedAgg[0]?.averageScore ?? 1
        : sortedAgg[0]?.totalScore ?? sortedAgg[0]?.averageScore ?? 1;

  const toolLabel = resolveSessionToolDescriptor(effectiveToolId)?.name ?? 'Prioritization';
  const layerTitle = showingTool
    ? `${session.name} — ${toolLabel}`
    : session.name;
  // P-LIVE-1: name the V3 Event step this live board is executing.
  const requestedStep = isV3StepId(boardSearchParams.get('step')) ? boardSearchParams.get('step') as StepId : undefined;
  const stepCtx = getV3StepContext(session.modeConfig, requestedStep);
  const stepPrefix = stepCtx
    ? `Step ${stepCtx.index} of ${stepCtx.total} — ${stepCtx.label}${stepCtx.isMainEvent ? ' (main event)' : ''} · `
    : '';
  const layerSubtitle = session.description
    ? `${stepPrefix}${toolLabel} · ${session.description.slice(0, 48)}${session.description.length > 48 ? '…' : ''}`
    : `${stepPrefix}${toolLabel}`;

  const contextPanelFooter =
        !showingTool ? (
          <div>
            {rankingError && (
              <p className="text-xs text-electric-mustard-600 mb-2 font-body">{rankingError}</p>
            )}
            {mySubmitted ? (
              <div className="space-y-2">
                <span className="flex items-center gap-1.5 text-xs text-alpine-green-400 font-heading tracking-wide uppercase">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Submitted
                </span>
                <button
                  type="button"
                  onClick={handleStartRanking}
                  className={`w-full min-h-[44px] font-heading text-[0.72rem] tracking-[0.06em] uppercase ${live.textTer} hover:text-[var(--text-pri)]`}
                >
                  Revise response
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartRanking}
                className="w-full min-h-[48px] h-12 flex items-center justify-center gap-2 bg-alpine-green-500/15 border border-alpine-green-500/45 font-heading text-[0.78rem] tracking-[0.1em] uppercase text-alpine-green-400 hover:bg-alpine-green-500/30 transition-colors"
              >
                <Play className="h-3.5 w-3.5" />
                Start ranking
              </button>
            )}
            {!isFacilitator && openCalibrationCount > 0 && (
              <button
                type="button"
                onClick={() => setShowCalibrationCard(true)}
                className="mt-2 w-full min-h-[44px] flex items-center justify-center gap-1.5 font-heading text-[0.72rem] tracking-[0.08em] uppercase text-electric-mustard-600 border border-electric-mustard-600/35 py-2 hover:bg-electric-mustard-600/10 transition-colors"
              >
                <Ruler className="h-3 w-3" />
                Calibrate scales ({openCalibrationCount})
              </button>
            )}
            {!isFacilitator && (
              <button
                type="button"
                onClick={() => { setConcernError(null); setShowConcernModal(true); }}
                className={`mt-2 w-full min-h-[40px] flex items-center justify-center gap-1.5 font-heading text-[0.7rem] tracking-[0.08em] uppercase ${live.textTer} hover:text-luminous-coral-700 transition-colors`}
              >
                <Flag className="h-3 w-3" />
                {concernSubmitted ? 'Concern submitted' : 'Raise a concern'}
              </button>
            )}
            {/* P3: say why, as it is said — lands in the session's Deliberation Record. */}
            <RoomReasonCapture
              sessionId={sessionId}
              workspaceId={liveWorkspaceId}
              authorId={userId}
              items={sessionToolItems}
            />
          </div>
        ) : undefined;

  const joinUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${joinPathFor(sessionId ?? '')}`;
  const copyJoinUrl = () => {
    void navigator.clipboard?.writeText(joinUrl).then(() => {
      setJoinCopied(true);
      window.setTimeout(() => setJoinCopied(false), 1600);
    });
  };

  // C07 / P3: "all in, you may reveal" — everyone expected has submitted. One
  // count, the bar's, also drives the host panel's line.
  const allIn = roomIsAllIn(responses.length, totalExpected);
  const facilitatorBar = isFacilitator ? (
    <div ref={hostBarRef} className="flex-shrink-0">
    <LiveFacilitatorBar>
      <div className={`text-sm ${allIn ? 'text-[var(--green)]' : live.textTer} flex items-center gap-1.5 font-body`} data-testid="room-all-in">
        {allIn ? <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" /> : <Clock className="h-3.5 w-3.5 flex-shrink-0" />}
        <span className="min-w-0">
          {allIn
            ? `All in — ${responses.length} of ${totalExpected} submitted. Close & save to lock the result, then reveal it.`
            : `Collecting responses — ${responses.length} of ${totalExpected} submitted`}
        </span>
      </div>
      {closeError && (
        <div className="flex items-center gap-1.5 text-sm text-luminous-coral-700 font-body">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="min-w-0 flex-1">{closeError}</span>
          <button type="button" onClick={() => setCloseError(null)} aria-label="Dismiss">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 overflow-x-auto pb-0.5 -mx-0.5 px-0.5 md:ml-auto md:overflow-visible md:pb-0">
        <button
          type="button"
          onClick={() => setShowHostControls((v) => !v)}
          aria-expanded={showHostControls}
          data-testid="host-controls-button"
          title="The host's controls: who has answered, the timer, names, pause, spotlight and broadcast"
          className={`inline-flex items-center gap-1.5 h-9 px-3.5 flex-shrink-0 font-heading text-[0.75rem] tracking-[0.07em] uppercase text-[var(--text-sec)] border border-[var(--rule-bright)] hover:bg-[var(--bg-raised)]/[0.04] transition-colors ${showHostControls ? 'bg-[var(--bg-raised)]/[0.08]' : ''}`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Host
        </button>
        <button
          type="button"
          onClick={() => setShowJoin((v) => !v)}
          aria-expanded={showJoin}
          data-testid="join-link-button"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 flex-shrink-0 font-heading text-[0.75rem] tracking-[0.07em] uppercase text-[var(--text-sec)] border border-[var(--rule-bright)] hover:bg-[var(--bg-raised)]/[0.04] transition-colors"
        >
          <Play className="h-3.5 w-3.5" />
          Join link
        </button>
        <button
          type="button"
          onClick={() => window.open(`/prioritization/sessions/${sessionId}/tv`, '_blank', 'noopener')}
          title="The shared display for the front of the room: who is in, the code to join, the reveal"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 flex-shrink-0 font-heading text-[0.75rem] tracking-[0.07em] uppercase text-[var(--text-sec)] border border-[var(--rule-bright)] hover:bg-[var(--bg-raised)]/[0.04] transition-colors"
        >
          <Tv className="h-3.5 w-3.5" />
          Board
        </button>
        <button
          type="button"
          onClick={() => setShowCalibrationPanel(true)}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 flex-shrink-0 font-heading text-[0.75rem] tracking-[0.07em] uppercase text-[var(--text-sec)] border border-[var(--rule-bright)] hover:bg-[var(--bg-raised)]/[0.04] transition-colors"
        >
          <Ruler className="h-3.5 w-3.5" />
          Calibrate
        </button>
        <button
          type="button"
          onClick={handleClose}
          disabled={isClosing}
          className={`inline-flex items-center gap-2 h-9 px-3.5 flex-shrink-0 font-heading text-[0.75rem] tracking-[0.07em] uppercase text-luminous-coral-700 border hover:bg-luminous-coral-700/10 disabled:opacity-50 transition-colors ${allIn ? 'border-luminous-coral-700 bg-luminous-coral-700/10' : 'border-luminous-coral-700/35'}`}
        >
          {isClosing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {isClosing ? 'Closing…' : 'Close & save'}
        </button>
      </div>
    </LiveFacilitatorBar>
      {showJoin && (
        <div
          data-testid="join-panel"
          className={`flex-shrink-0 flex items-start gap-4 px-5 py-3 border-b ${live.border} ${live.raised}`}
        >
          <JoinQr url={joinUrl} size={128} />
          <div className="min-w-0 flex flex-col gap-1.5 font-body text-sm">
            <div className={live.textTer}>Phones join here. The Board shows the same code large, for the room.</div>
            <div className="font-mono text-xs break-all text-[var(--text-pri)]">{joinUrl}</div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={copyJoinUrl}
                className="inline-flex items-center h-8 px-3 font-heading text-[0.7rem] tracking-[0.07em] uppercase text-[var(--text-sec)] border border-[var(--rule-bright)] hover:bg-[var(--bg-raised)]/[0.04] transition-colors"
              >
                {joinCopied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : undefined;

  // The ball is always named (Doc 26 rule 4): who the session is waiting on,
  // in one line, for every viewer. Falls back to role words, never raw ids.
  const facilitatorDisplayName = session.facilitatorId
    ? resolveLiveParticipantDisplayName(session.facilitatorId, participantNameById)
    : 'Participant';
  const facilitatorLabel =
    facilitatorDisplayName === 'Participant' ? 'the facilitator' : facilitatorDisplayName;
  const ballLine = isFacilitator
    ? `The ball is with the room — ${responses.length} of ${totalExpected} in. Close the session to lock the result.`
    : mySubmitted
      ? `Your ranking is in. The ball is with ${facilitatorLabel} — the result locks when they close the session.`
      : 'The ball is with you — add your ranking.';

  return (
    <LiveSessionProgressProvider>
      <LiveBoardChrome
        showingTool={showingTool}
        sessionId={sessionId}
        toolSessionId={toolSessionId}
        liveWorkspaceId={liveWorkspaceId}
        session={session}
        ballLine={ballLine}
        sessionToolItems={sessionToolItems}
        userId={userId}
        userName={userName}
        layerTitle={layerTitle}
        layerSubtitle={layerSubtitle}
        toolLabel={toolLabel}
        effectiveToolId={effectiveToolId}
        onBackToSession={() => navigate(`/prioritization/sessions/${sessionId}`)}
        onBackToBoard={() => { setShowingTool(false); setRankingError(null); }}
        onToolComplete={handleToolComplete}
        onToolError={setRankingError}
        toolError={rankingError}
        facilitatorBar={facilitatorBar}
        contextPanelFooter={contextPanelFooter}
        displayParticipantIds={displayParticipantIds}
        responses={responses}
        participantNameById={participantNameById}
        sortedAgg={sortedAgg}
        outputType={outputType}
        maxScore={maxScore}
        myResults={myResponse?.results}
      />
      {showConcernModal && (
        <RaiseConcernModal
          onSubmit={handleRaiseConcern}
          onClose={() => setShowConcernModal(false)}
          isSubmitting={concernSubmitting}
          error={concernError}
        />
      )}
      {showCalibrationPanel && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col bg-[var(--bg-raised)] shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)]">
              <span className="text-sm font-semibold text-[var(--text-pri)]">Scale calibration</span>
              <button onClick={() => setShowCalibrationPanel(false)} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <ScaleCalibrationPanel sessionId={sessionId} />
            </div>
          </div>
        </div>
      )}
      {showCalibrationCard && (
        <ScaleCalibrationCard
          sessionId={sessionId}
          participantId={userId}
          participantName={userName}
          onClose={() => setShowCalibrationCard(false)}
        />
      )}
      {/* The host's controls: roster, timer, room settings, pause, spotlight, broadcast */}
      {isFacilitator && (
        <FacilitatorConductorPanel
          session={session}
          toolSessionId={toolSessionId}
          items={sessionToolItems}
          isPaused={isPausedLive}
          isOpen={showHostControls}
          onClose={() => setShowHostControls(false)}
          allIn={allIn}
          topOffset={hostBarBottom}
        />
      )}
      {/* Pause gate for participants — the facilitator must stay in control */}
      {!isFacilitator && <SessionPausedOverlay sessionId={sessionId} />}
      {/* The conductor's spotlight, shown to everyone (the facilitator sees what the room sees) */}
      <ItemSpotlightDrawer sessionId={sessionId} />
    </LiveSessionProgressProvider>
  );
}

// ─── Chrome + board / tool layout ───────────────────────────────────────────

interface LiveBoardChromeProps {
  showingTool: boolean;
  sessionId: string;
  toolSessionId: string;
  liveWorkspaceId: string;
  session: PrioritizationSession;
  /** One-line ball-position statement shown on the board (Doc 26 rule 4). */
  ballLine: string;
  sessionToolItems: ToolItem[];
  userId: string;
  userName: string;
  layerTitle: string;
  layerSubtitle: string;
  toolLabel: string;
  effectiveToolId: string | null | undefined;
  onBackToSession: () => void;
  onBackToBoard: () => void;
  onToolComplete: () => void;
  onToolError: (msg: string) => void;
  /** Why the last answer was not saved — shown on the answering screen too, so a refusal is never silent. */
  toolError?: string | null;
  facilitatorBar?: ReactNode;
  contextPanelFooter?: ReactNode;
  displayParticipantIds: string[];
  responses: Array<{ participantId: string }>;
  participantNameById: Record<string, string>;
  sortedAgg: AggregateResult[];
  outputType: ToolOutputType;
  maxScore: number;
  /** The current participant's own submitted results (undefined until they've submitted). */
  myResults?: ToolResultItem[];
}

function LiveBoardChrome({
  showingTool,
  sessionId,
  toolSessionId,
  liveWorkspaceId,
  session,
  ballLine,
  sessionToolItems,
  userId,
  userName,
  layerTitle,
  layerSubtitle,
  toolLabel,
  effectiveToolId,
  onBackToSession,
  onBackToBoard,
  onToolComplete,
  onToolError,
  toolError,
  facilitatorBar,
  contextPanelFooter,
  displayParticipantIds,
  responses,
  participantNameById,
  sortedAgg,
  outputType,
  maxScore,
  myResults,
}: LiveBoardChromeProps) {
  const isMobile = useMobileExperience();
  const progress = useLiveSessionProgress();
  const placed = progress?.placed ?? 0;
  const total = progress?.total ?? sessionToolItems.length;
  const progressPct = total > 0 ? Math.round((placed / total) * 100) : undefined;

  useEffect(() => {
    if (!showingTool) progress?.resetProgress();
  }, [showingTool, progress]);

  // S-3: when names are hidden the roster shows counts — your own row, and no one else's.
  const { rules: roomRules } = useSessionRoomRules(sessionId);
  const hideNames = namesHidden(roomRules, 'room');
  // S-2: the host's timer, seen by everyone in the room.
  const roomTimer = useRoomTimer(sessionId, toolSessionId);

  const participantRows: LiveParticipantRow[] = displayParticipantIds
    .filter(uid => !hideNames || uid === userId)
    .map(uid => {
    const submitted = responses.some(r => r.participantId === uid);
    const isMe = uid === userId;
    const name = isMe
      ? userName || resolveLiveParticipantDisplayName(uid, participantNameById)
      : resolveLiveParticipantDisplayName(uid, participantNameById);
    return {
      id: uid,
      name,
      initials: avatarInitials(isMe ? (userName || name) : name),
      submitted,
      isMe,
      progressPct: isMe && showingTool ? progressPct : undefined,
    };
  });

  const submittedCount = responses.length;
  const totalCount = displayParticipantIds.length;

  // Phone: Start ranking lives in the bottom dock; sheet shows people only.
  const contextPanel = (
    <LiveContextPanel
      methodLabel={toolLabel}
      itemCount={sessionToolItems.length}
      participants={participantRows}
      submittedCount={submittedCount}
      totalCount={totalCount}
      footer={isMobile ? undefined : contextPanelFooter}
      variant={isMobile ? 'sheet' : 'rail'}
    />
  );

  const layerTrailing = showingTool ? (
    <>
      <span className={`font-heading text-[0.75rem] ${live.textDim} tabular-nums`}>
        <span className="text-alpine-green-400 font-bold">{placed}</span> / {total}
      </span>
      <LiveStatusPulse />
    </>
  ) : (
    <LiveStatusPulse />
  );

  return (
    <LiveSessionViewport>
      <LiveLayerBar
        backLabel={showingTool ? '← Board' : '← Session'}
        onBack={showingTool ? onBackToBoard : onBackToSession}
        title={layerTitle}
        subtitle={layerSubtitle}
        trailing={layerTrailing}
      />
      {/* The conductor's "Broadcast to all" banner. It draws in theme colors, so it
          sits on the theme surface rather than this always-dark board. */}
      <div className="flex-shrink-0 bg-[var(--bg-raised)]">
        <AnnouncementBanner sessionId={sessionId} />
        {showingTool && toolError && (
          <p role="alert" className="px-4 py-2 border-b border-[var(--rule)] font-body text-sm text-[var(--coral)]" data-testid="room-answer-error">
            {toolError}
          </p>
        )}
        {roomTimer.endsAt !== null && (
          <div className="flex items-center justify-center gap-3 px-4 py-2 border-b border-[var(--rule)]" data-testid="room-timer-strip">
            <RoomCountdown
              endsAt={roomTimer.endsAt}
              atZeroLabel={roomTimer.atZero === 'lock' ? 'Time is up · answers are locked' : 'Time is up'}
            />
          </div>
        )}
      </div>
      <LiveRoomLayout
        facilitatorBar={facilitatorBar}
        contextPanel={contextPanel}
        peopleLabel={`People ${submittedCount}/${totalCount}`}
        mobileDock={!showingTool ? contextPanelFooter : undefined}
        main={
          showingTool ? (
            <LiveToolRunner
              embedded
              sessionId={sessionId}
              toolSessionId={toolSessionId}
              workspaceId={liveWorkspaceId}
              selectedToolId={effectiveToolId}
              toolConfig={session?.toolConfig}
              items={sessionToolItems}
              participantId={userId}
              participantName={userName}
              onComplete={onToolComplete}
              onBack={onBackToBoard}
              onError={onToolError}
            />
          ) : (
            <LiveBoardMain
              session={session}
              ballLine={ballLine}
              toolLabel={toolLabel}
              sortedAgg={sortedAgg}
              outputType={outputType}
              maxScore={maxScore}
              totalItems={sessionToolItems.length}
              myResults={myResults}
              toolId={effectiveToolId}
            />
          )
        }
      />
    </LiveSessionViewport>
  );
}

/** A single participant's own submitted results — stable regardless of what anyone else submits. */
function MyResultsSection({ results, outputType, toolId }: { results: ToolResultItem[]; outputType: ToolOutputType; toolId?: string | null }) {
  const sorted = useMemo(() => sortMyResultsForDisplay(results, outputType), [results, outputType]);
  return (
    <div className="mb-6">
      <div className={`font-heading text-[0.68rem] font-semibold tracking-[0.18em] uppercase text-electric-mustard-600/90 mb-3`}>
        Your ranking
      </div>
      <div className="space-y-2">
        {sorted.map((item, idx) => (
          <div
            key={item.id}
            className={`flex items-center gap-2.5 ${live.surface} border ${live.border}  px-4 py-3`}
          >
            <span className="font-heading text-[0.78rem] w-5 text-electric-mustard-600">{idx + 1}</span>
            <span className={`font-body text-[0.95rem] flex-1 truncate ${live.textSec}`}>{item.name}</span>
            <span className="font-heading text-[0.75rem] tabular-nums text-electric-mustard-600">
              {formatMyResultMetric(item, outputType, toolId)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LiveBoardMain({
  session,
  ballLine,
  toolLabel,
  sortedAgg,
  outputType,
  maxScore,
  totalItems,
  myResults,
  toolId,
}: {
  session: { name: string; decisionPrompt?: string | null };
  ballLine: string;
  toolLabel: string;
  sortedAgg: AggregateResult[];
  outputType: ToolOutputType;
  maxScore: number;
  totalItems: number;
  myResults?: ToolResultItem[];
  /** The method's id, for how each row's number reads (Buy-a-Feature reads in dollars). */
  toolId: string | null | undefined;
}) {
  const isMobile = useMobileExperience();
  return (
    <div className={`flex-1 overflow-y-auto ${isMobile ? 'px-4 py-4' : 'px-6 py-6'}`}>
      <div className="max-w-2xl">
        <div className={`font-heading text-[0.72rem] tracking-[0.2em] uppercase ${live.textDim} mb-1.5`}>
          {toolLabel} · Live session
        </div>
        <h2
          className={`font-display font-light text-[var(--text-pri)] leading-tight mb-2 ${
            isMobile ? 'text-[1.2rem]' : 'text-[1.35rem]'
          }`}
        >
          {session.name}
        </h2>
        <p className={`font-body text-[0.85rem] font-light ${live.textTer} leading-relaxed mb-2`}>
          {ballLine}
        </p>
        {session.decisionPrompt && (
          <p className={`font-body text-[0.95rem] font-light ${live.textSec} leading-relaxed mb-6`}>
            {session.decisionPrompt}
          </p>
        )}

        {myResults && myResults.length > 0 && (
          <MyResultsSection results={myResults} outputType={outputType} toolId={normalizeSessionToolId(toolId)} />
        )}

        <div className={`font-heading text-[0.68rem] font-semibold tracking-[0.18em] uppercase ${live.textDim} mb-3`}>
          {sortedAgg.length > 0 ? 'Live aggregate' : 'Waiting for submissions'}
        </div>

        {sortedAgg.length === 0 ? (
          <div
            className={`${live.surface} border ${live.border}  ${
              isMobile ? 'p-5' : 'p-8'
            } flex flex-col items-center ${live.textTer}`}
          >
            <Clock className="h-8 w-8 mb-3 opacity-40" />
            <p className="text-base font-body text-center">Results appear as participants submit.</p>
            <p className={`text-sm mt-1 text-center ${live.textDim}`}>
              {totalItems} items · {isMobile ? 'tap Start ranking below' : 'use Start ranking in the panel →'}
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {sortedAgg.map((agg, idx) => {
              const pct = aggregateBarPercent(agg, outputType, maxScore);
              const metric = formatAggregateMetric(agg, outputType, normalizeSessionToolId(toolId));
              const isTop = idx === 0;
              return (
                <div
                  key={agg.itemId}
                  className={`${live.surface} border ${live.border}  px-4 py-3.5 ${
                    isTop ? 'border-l-2 border-l-alpine-green-500/60' : ''
                  }`}
                >
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className={`font-heading text-[0.78rem] w-5 ${isTop ? 'text-alpine-green-400' : live.textDim}`}>
                      {idx + 1}
                    </span>
                    <span className={`font-body text-[0.95rem] flex-1 truncate ${isTop ? 'text-[var(--text-pri)]' : live.textSec}`}>
                      {agg.itemTitle}
                    </span>
                    <span className={`font-heading text-[0.75rem] tabular-nums ${isTop ? 'text-alpine-green-400' : live.textTer}`}>
                      {metric}
                    </span>
                  </div>
                  <div className={`h-0.5 ${live.borderBright} bg-[var(--rule-bright)] rounded-full overflow-hidden`}>
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        isTop ? 'bg-alpine-green-500/75' : 'bg-[var(--slate)]/80'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Page shell ─────────────────────────────────────────────────────────────

export default function LiveSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoStartRanking = searchParams.get('startRanking') === '1';
  /** Event Designer already sent Live-now + My Tasks via inviteStepRun — skip duplicate bells. */
  const skipGoLiveNotify = searchParams.get('invited') === '1';
  const requestedStep = isV3StepId(searchParams.get('step')) ? searchParams.get('step') as StepId : undefined;
  const [toolSessionId, setToolSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessions = useSessionStore(s => s.sessions);
  const fetchSessionById = useSessionStore(s => s.fetchSessionById);
  const session = sessions.find(s => s.id === sessionId);
  const createToolSession = useToolSessionStore(s => s.createToolSession);
  const subscribeToToolSession = useToolSessionStore(s => s.subscribeToToolSession);
  const toolSessions = useToolSessionStore(s => s.toolSessions);
  const userId = useUserStore(s => s.userId) ?? '';
  const userName = useUserStore(s => s.userName) ?? 'Facilitator';

  // Load participants early — needed for auto-create participant list
  const fetchParticipantsForSession = useSessionParticipantsStore(s => s.fetchParticipantsForSession);
  const allStoredParticipants = useSessionParticipantsStore(s => s.participants);
  useEffect(() => {
    if (sessionId) void fetchParticipantsForSession(sessionId);
  }, [sessionId, fetchParticipantsForSession]);

  // Session-row realtime (brief F): the facilitator's Close transitions the
  // session to RUN_COMPLETE and emits `result_locked`; this subscription is
  // what carries that lock to every connected participant so the redirect
  // below lands them all on the §6.1 confirmation together (Doc 26 rule 6).
  useEffect(() => {
    if (!sessionId) return;
    void useSessionStore.getState().subscribeToSessionRealtime(sessionId);
    return () => {
      useSessionStore.getState().unsubscribeFromSessionRealtime(sessionId);
    };
  }, [sessionId]);

  // Polling fallback for the lock, mirroring the response poll's rationale:
  // Realtime + RLS can silently drop cross-user events, and a missed lock
  // strands a participant on a dead board.
  useEffect(() => {
    if (!sessionId) return;
    const interval = setInterval(() => {
      void (async () => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        const { data } = await supabase
          .from('prioritization_sessions')
          .select('state')
          .eq('id', sessionId)
          .maybeSingle();
        const state = (data as { state?: string } | null)?.state;
        if (state === 'RUN_COMPLETE' || state === 'PUBLISHED') {
          void useSessionStore.getState().fetchSessionById(sessionId);
        }
      })();
    }, 5000);
    return () => clearInterval(interval);
  }, [sessionId]);
  const sessionParticipantRows = allStoredParticipants.filter(p => p.sessionId === sessionId);
  const allItems = useItemsStore(s => s.items);
  const fetchItemsFromSupabase = useItemsStore(s => s.fetchFromSupabase);
  // R4: once the session is open for ranking, items read as they did then, not as later edited.
  const startWordingIds = useMemo(() => {
    const v3 = ((session?.modeConfig as { v3Config?: { items?: Array<{ id: string }> } } | undefined)?.v3Config?.items ?? []).map((i) => i.id);
    return [...(session?.itemIds ?? []), ...v3];
  }, [session?.itemIds, session?.modeConfig]);
  const startWording = useSessionStartWording(startWordingIds, session?.criteriaFinalizedAt);
  const [autoCreating, setAutoCreating] = useState(false);

  // Keep workspace items fresh so sessionToolItems resolves correctly for B2B sessions
  useEffect(() => {
    if (session) void fetchItemsFromSupabase();
  }, [session, fetchItemsFromSupabase]);

  // As soon as we know the toolSessionId, subscribe to its realtime row so
  // toolSession.items is populated before we try to show the tool.
  useEffect(() => {
    if (toolSessionId) void subscribeToToolSession(toolSessionId);
  }, [toolSessionId, subscribeToToolSession]);

  const toolSession = toolSessions.find(s => s.id === toolSessionId);
  const v3Config = (session?.modeConfig as Record<string, unknown> | undefined)?.v3Config as
    | {
        primaryGoalStep?: StepId;
        parentStep?: StepId;
        steps?: Partial<Record<StepId, { toolId?: string }>>;
        participants?: Array<{ userId?: string; name?: string }>;
        items?: Array<{ id: string; title?: string; name?: string }>;
        criteria?: Array<{ id: string; name: string }>;
      }
    | undefined;
  const activeV3Step: StepId | undefined =
    requestedStep ?? (isV3StepId(v3Config?.parentStep) ? v3Config.parentStep : undefined) ?? v3Config?.primaryGoalStep;

  /**
   * Canonical item list for this live session.
   *
   * Resolution order:
   * 1. S1/S3 special pools (participants / criteria)
   * 2. v3Config.items when present — Event Designer working pool is authoritative
   *    (session.item_ids can lag and was dropping items from collaborative runs)
   * 3. Workspace items matched by session.itemIds (B2B / legacy)
   * 4. tool_session.items snapshot
   */
  const sessionToolItems: ToolItem[] = useMemo(() => {
    if (!session) return [];

    if (activeV3Step === 'S1' && (v3Config?.participants ?? []).length > 0) {
      return (v3Config?.participants ?? []).map((p, idx) => ({
        id: p.userId ?? `participant_${idx}`,
        name: p.name ?? p.userId ?? `Participant ${idx + 1}`,
        description: '',
        sourceId: p.userId ?? `participant_${idx}`,
      }));
    }

    if (activeV3Step === 'S3' && (v3Config?.criteria ?? []).length > 0) {
      return (v3Config?.criteria ?? []).map(c => ({
        id: c.id,
        name: c.name,
        description: '',
        sourceId: c.id,
      }));
    }

    const v3Items = v3Config?.items ?? [];
    if (v3Items.length > 0) {
      return v3Items.map(i => {
        const workspace = allItems.find((w) => w.id === i.id);
        const shown = workspace ? startWording(i.id, { title: workspace.title, description: workspace.description }) : null;
        return {
          id: i.id,
          name: shown?.title ?? i.title ?? i.name ?? 'Item',
          description: shown?.description ?? '',
          sourceId: i.id,
        };
      });
    }

    if (session.itemIds?.length) {
      const fromWorkspace = allItems
        .filter(i => session.itemIds.includes(i.id))
        .map(i => {
          const shown = startWording(i.id, { title: i.title ?? 'Item', description: i.description });
          return { id: i.id, name: shown.title, description: shown.description, sourceId: i.id };
        });
      if (fromWorkspace.length > 0) return fromWorkspace;
    }

    if (toolSession?.items?.length) {
      return toolSession.items.map(i => ({
        id: i.id,
        // The Apps write tool-session items as { id, name } (sessionAppActions
        // sessionToolItems); other paths write { id, title }. Read both.
        name: i.title ?? (i as typeof i & { name?: string }).name ?? 'Item',
        description: '',
        sourceId: i.id,
      }));
    }

    // S3 (Criteria) — the tool operates on criteria, not workspace items.
    // Criteria live at v3Config.criteria, not v3Config.items.
    if (v3Config?.primaryGoalStep === 'S3' && (v3Config?.criteria ?? []).length > 0) {
      return (v3Config.criteria ?? []).map(c => ({
        id: c.id,
        name: c.name,
        description: '',
        sourceId: c.id,
      }));
    }

    return [];
  }, [session, activeV3Step, v3Config, allItems, toolSession?.items, startWording]);

  // Ensure session is in store
  useEffect(() => {
    if (!sessionId) return;
    if (!session) {
      void fetchSessionById(sessionId);
    }
  }, [sessionId, session, fetchSessionById]);

  // Find the active tool_session for this prioritization session.
  // Also subscribes to Realtime so the board appears automatically when the
  // facilitator opens the session — no manual refresh needed.
  useEffect(() => {
    if (!sessionId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) { setError('Database not available'); setLoading(false); return; }

    let cancelled = false;

    // Initial fetch
    supabase
      .from('tool_sessions')
      .select('id')
      .eq('session_id', sessionId)
      .in('phase', ['collecting', 'revealed'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) { setError(err.message); setLoading(false); return; }
        if (data) setToolSessionId((data as { id: string }).id);
        setLoading(false);
      });

    // Realtime: pick up when facilitator creates the tool_session (INSERT)
    // so participants don't need to manually refresh.
    const channel = supabase
      .channel(`live-page-tool-session:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tool_sessions',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          if (cancelled) return;
          const row = payload.new as { id: string; phase: string };
          if (row.phase === 'collecting' || row.phase === 'revealed') {
            setToolSessionId(row.id);
            setLoading(false);
            setError(null);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [sessionId]);

  // If session is closed, land every participant on the result artifact —
  // the lock moment (Doc 26 rule 6) — never on the reports list.
  useEffect(() => {
    if (session && (session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED')) {
      navigate(`/prioritization/reports/session/${session.id}`, {
        replace: true,
        state: { lockMoment: true },
      });
    }
  }, [session, sessionId, navigate]);

  // Auto-create tool session when arriving via ?startRanking=1 (V3 Event "Open room" deep-link)
  // and no tool session has been started yet. This is the facilitator opening their own session.
  useEffect(() => {
    if (!autoStartRanking || loading || toolSessionId || autoCreating) return;
    if (!session || !sessionId) return;

    const workspaceId: string = session.workspaceId ?? getCurrentWorkspaceId() ?? '';
    if (!workspaceId || !userId) return;

    // Resolve items for the specific V3 step that launched this room.
    const launchStep: StepId = activeV3Step ?? 'S5';
    const sessionItemIds: string[] = (session as unknown as { item_ids?: string[] }).item_ids ?? session.itemIds ?? [];
    const v3Items: Array<{ id: string; title?: string; name?: string }> = v3Config?.items ?? [];
    const v3Criteria: Array<{ id: string; name: string }> = v3Config?.criteria ?? [];
    const v3Participants: Array<{ userId?: string; name?: string }> = v3Config?.participants ?? [];

    // V3 Event Designer: v3Config.items is the working pool. Prefer it over
    // session.item_ids, which can lag (e.g. only one of four platform ids synced).
    const resolvedItems =
      launchStep === 'S1' && v3Participants.length > 0
        ? v3Participants.slice(0, 50).map((p, idx) => ({
            id: p.userId ?? `participant_${idx}`,
            title: p.name ?? p.userId ?? `Participant ${idx + 1}`,
            rank: idx,
          }))
        : launchStep === 'S3' && v3Criteria.length > 0
        ? v3Criteria.slice(0, 50).map((c, idx) => ({ id: c.id, title: c.name, rank: idx }))
        : v3Items.length > 0
          ? v3Items.slice(0, 50).map((it, idx) => {
              const match = allItems.find((i) => i.id === it.id);
              return {
                id: it.id,
                title: (match ? startWording(it.id, { title: match.title }).title : undefined) ?? it.title ?? it.name ?? `Item ${idx + 1}`,
                rank: idx,
              };
            })
          : sessionItemIds.slice(0, 50).map((id, idx) => {
              const match = allItems.find((i) => i.id === id);
              return { id, title: match ? startWording(id, { title: match.title }).title : `Item ${idx + 1}`, rank: idx };
            });

    // The method: the launched step's tool for a designed event, else the one
    // the session declares. The output type follows the method (one counting rule).
    const launch = resolveLaunchTool({
      selectedToolId: session.selectedToolId,
      v3Config,
      step: launchStep,
    });

    // Resolve all participants from session_participants table (authoritative source).
    // v3Config.participants is NOT persisted — session_participants is the real store.
    // sessionParticipantRows is pre-fetched by the outer page on mount.
    const allParticipantIds = Array.from(new Set([
      userId,
      ...sessionParticipantRows.map(p => p.userId).filter(Boolean),
    ]));
    const participantNamesMap: Record<string, string> = { [userId]: userName };
    // Names are resolved later by the board — seed with userId slice as placeholder
    for (const p of sessionParticipantRows) {
      if (p.userId && p.userId !== userId) {
        participantNamesMap[p.userId] = p.userId.slice(0, 8);
      }
    }

    setAutoCreating(true);
    void createToolSession({
      sessionId,
      workspaceId,
      toolType: launch.toolId,
      toolName: launch.toolName,
      outputType: launch.outputType,
      mode: 'sync',
      participantIds: allParticipantIds,
      participantNames: participantNamesMap,
      facilitatorId: userId,
      facilitatorName: userName,
      items: resolvedItems,
    }).then((id) => {
      if (id) setToolSessionId(id);

      // GAP-4: notify participants → /participate (fix prior broken /session/ URL).
      // Event Designer passes invited=1 when it already notified + created My Tasks.
      const participantUserIds = allParticipantIds.filter(pid => pid && pid !== userId);

      if (participantUserIds.length > 0 && !skipGoLiveNotify) {
        // Take the row live first: the urgent "Live now · Join now" must not
        // point at a session the participant surface still calls a draft.
        void ensureSessionJoinable(session.id, userId, {
          participantCount: Math.max(1, allParticipantIds.length),
        }).then((goLive) => {
          if (!goLive.wentLive) {
            console.warn('[LiveSessionPage] session not announced as live:', goLive.error);
            return;
          }
          void notifySessionLiveNow({
            sessionId: session.id,
            sessionName: session.name,
            recipientIds: participantUserIds,
            cycleId: session.cycleId,
            senderId: userId,
            assumeLive: true,
          });
        });
      }
      if (participantUserIds.length > 0 || skipGoLiveNotify) {
        // Emit waiting_room_started so any participant already on the waiting room
        // advances automatically (mirrors B2B SessionDetail behaviour)
        void import('../../../lib/sessionEvents').then(({ emitSessionEvent }) =>
          emitSessionEvent(session.id, userId, userName, 'waiting_room_started', { toolSessionId: id ?? '' })
        );
      }
    }).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    }).finally(() => {
      setAutoCreating(false);
    });
  }, [autoStartRanking, skipGoLiveNotify, loading, toolSessionId, autoCreating, session, sessionId, userId, userName, allItems, sessionParticipantRows, createToolSession, activeV3Step, v3Config, startWording]);

  if (!sessionId) return null;

  // Gate: hold the spinner until the tool session row is in the store AND
  // (if the session has items to load) those items have resolved.
  //
  // Rule: "itemsLoading" is true only while the tool session row hasn't arrived
  // yet AND items haven't resolved from an inline source (v3Config / session.itemIds).
  // Once the tool session is confirmed in the store, an empty item list is
  // intentional — S3 Criteria sessions legitimately have no items.
  const toolSessionConfirmed = toolSessionId != null && toolSession != null;
  const itemsLoading =
    toolSessionId != null && !toolSessionConfirmed && sessionToolItems.length === 0;

  if (loading || autoCreating || (autoStartRanking && !toolSessionId && !error) || itemsLoading) {
    return (
      <LiveSessionViewport>
        <div className="flex flex-1 items-center justify-center gap-3">
          <Loader2 className="h-8 w-8 text-[var(--text-ter)] animate-spin" />
          <span className="text-sm text-[var(--text-ter)] font-body">
            {autoCreating ? 'Starting session…' : itemsLoading ? 'Loading session items…' : 'Loading…'}
          </span>
        </div>
      </LiveSessionViewport>
    );
  }

  if (error || !toolSessionId) {
    return (
      <LiveSessionViewport>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center px-6">
          <AlertCircle className="h-10 w-10 text-electric-mustard-600" />
          <p className="text-[var(--text-pri)] font-medium font-body">
            {error ? 'Could not start session' : 'Session not open yet'}
          </p>
          <p className="text-sm text-[var(--text-ter)] font-body max-w-md">
            {error ?? 'No active tool session found. If you just clicked "Go live", wait a moment and retry.'}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                const supabase = getSafeSupabaseClient();
                if (!supabase || !sessionId) { setLoading(false); return; }
                supabase
                  .from('tool_sessions')
                  .select('id')
                  .eq('session_id', sessionId)
                  .in('phase', ['collecting', 'revealed'])
                  .order('created_at', { ascending: false })
                  .limit(1)
                  .maybeSingle()
                  .then(({ data }) => {
                    if (data) setToolSessionId((data as { id: string }).id);
                    setLoading(false);
                  });
              }}
              className="flex items-center gap-1.5 border border-[var(--text-dim)] bg-[var(--surface-raised)] px-3 py-1.5 text-sm text-[var(--text-pri)] hover:border-[var(--slate)] hover:bg-[var(--surface-raised)]"
            >
              <Loader2 className="h-3 w-3" /> Retry
            </button>
            {autoStartRanking && session && !error && (
              <span className="text-xs text-[var(--text-dim)] font-heading uppercase tracking-wider flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" /> Listening for updates
              </span>
            )}
          </div>
        </div>
      </LiveSessionViewport>
    );
  }

  return (
    <LiveBoard
      sessionId={sessionId}
      toolSessionId={toolSessionId}
      sessionToolItems={sessionToolItems}
      autoStartRanking={autoStartRanking}
      onRankingStarted={() => setSearchParams({}, { replace: true })}
    />
  );
}
